/**
 * Where today falls in the saved schedules: who is on call now, and a
 * physician's next week. Both look across every saved schedule, not only the
 * one on screen, so "Your next call" is right near the end of a schedule
 * when the next one is already saved, and right on an older schedule opened
 * from History. Pure; the server hands it the saved weeks.
 *
 * Today is matched against the true on-call range, not the Monday-to-Sunday
 * week: on the holiday Monday after a long weekend the physician of the
 * 8-day week is still on call, and the next physician starts on the Tuesday.
 */
import { compareDates, type ISODate } from '../dates/iso.ts';

export interface DatedWeek {
  weekStart: ISODate;
  onCallStart: ISODate;
  onCallEnd: ISODate;
  physicianId: string | null;
}

/** The week whose on-call range contains today, or null between schedules. */
export function weekOnCallNow<W extends DatedWeek>(weeks: readonly W[], today: ISODate): W | null {
  return weeks.find((w) => compareDates(w.onCallStart, today) <= 0 && compareDates(today, w.onCallEnd) <= 0) ?? null;
}

/**
 * The physician's first week that starts after today. A week they are on
 * call for right now is not "next": the screen shows that one as on call now.
 */
export function nextWeekFor<W extends DatedWeek>(weeks: readonly W[], physicianId: string, today: ISODate): W | null {
  let best: W | null = null;
  for (const w of weeks) {
    if (w.physicianId !== physicianId || compareDates(w.onCallStart, today) <= 0) continue;
    if (best === null || compareDates(w.onCallStart, best.onCallStart) < 0) best = w;
  }
  return best;
}

export interface WeeksAgainstToday {
  /** How many weeks at the top are already over: folded away on screen until asked for. */
  earlier: number;
  /** The row on call today, or -1. */
  nowIndex: number;
  /** Every week is over: an older schedule, shown whole, with nothing "now" or "next". */
  finished: boolean;
}

/**
 * How one schedule's weeks (in date order) sit against today. A finished
 * schedule hides nothing: someone opening it from History came to see it all.
 */
export function weeksAgainstToday(weeks: readonly DatedWeek[], today: ISODate): WeeksAgainstToday {
  const firstNotOver = weeks.findIndex((w) => compareDates(w.onCallEnd, today) >= 0);
  const nowIndex = weeks.findIndex((w) => compareDates(w.onCallStart, today) <= 0 && compareDates(today, w.onCallEnd) <= 0);
  if (firstNotOver === -1) return { earlier: 0, nowIndex: -1, finished: weeks.length > 0 };
  return { earlier: firstNotOver, nowIndex, finished: false };
}
